// ==========================================================================
// Action Service - Action API calls
// Endpoints: /api/user/actions/*
// ==========================================================================

import api from '@/lib/axios';
import type {
  UserAction,
  UserActionDetail,
  UserActionStep,
  GetActionsResponse,
  GetActionDetailResponse,
  ActivateActionResponse,
  CompleteStepResponse,
  UploadEvidenceResponse,
} from '@/types/action.types';

/**
 * Get all user actions
 * @param status - Optional filter: 'active', 'assigned', 'completed', 'expired'
 */
export async function getActions(status?: string): Promise<UserAction[]> {
  const params = status ? { status } : {};
  const response = await api.get<GetActionsResponse>('/user/actions', { params });
  return response.data.actions;
}

/**
 * Get action detail with all steps
 */
export async function getActionDetail(actionId: number): Promise<UserActionDetail> {
  const response = await api.get<GetActionDetailResponse>(`/user/actions/${actionId}`);
  const data = response.data;

  // API detail endpoint doesn't return progress — compute from steps
  if (!data.progress && data.steps) {
    data.progress = {
      completed_steps: data.steps.filter((s) => s.status === 'completed').length,
      total_steps: data.steps.length,
    };
  }

  return data;
}

/**
 * Activate an assigned action
 * Changes status from 'assigned' to 'active'
 */
export async function activateAction(actionId: number): Promise<UserAction> {
  const response = await api.post<ActivateActionResponse>(`/user/actions/${actionId}/activate`);
  return response.data;
}

/**
 * Complete a step
 * For non-evidence steps: changes from 'pending' to 'completed'
 * For evidence steps: must be 'approved' first
 */
export async function completeStep(actionId: number, stepId: number): Promise<UserActionStep> {
  const response = await api.post<CompleteStepResponse>(
    `/user/actions/${actionId}/steps/${stepId}/complete`
  );
  return response.data.step;
}

/**
 * Upload evidence for a step
 * Changes status from 'pending' or 'rejected' to 'evidence_uploaded'
 * @param file - Image file (jpg, jpeg, png, webp, max 10MB)
 *
 * Image files are compressed client-side before upload (target 1MB max,
 * 1920px max dimension). Telegram Desktop has effective file-size limits
 * on Mini-App uploads that the unmodified phone-camera output blows past;
 * compressing here is the reliable cross-client fix. Non-image files
 * (none expected for evidence today) are uploaded as-is.
 */
export async function uploadEvidence(
  actionId: number,
  stepId: number,
  file: File
): Promise<UserActionStep> {
  const compressed = await maybeCompressImage(file);
  const formData = new FormData();
  formData.append('file', compressed);

  const response = await api.post<UploadEvidenceResponse>(
    `/user/actions/${actionId}/steps/${stepId}/upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data.step;
}

/**
 * Run browser-image-compression on image MIME types and pass everything
 * else through. Falls back to the original file if compression throws so
 * the upload still happens.
 */
async function maybeCompressImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) {
    return file;
  }

  try {
    const { default: imageCompression } = await import('browser-image-compression');
    return await imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    });
  } catch {
    return file;
  }
}
